import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import "./ReportsSectionCompact.css";
import {
  BTN_OUTLINE,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import {
  exportCashBalanceReport,
  getCashBalanceReport,
} from "../api/reports";
import { useAuth } from "../context/AuthContext";
import { toISODateString } from "../utils/dateOnly";
import { getSalonTimezone } from "../utils/salonTimezone";
import useTableSort from "../hooks/useTableSort";
import { CASH_BALANCE_TOOLTIPS } from "../constants/reportTooltips";
import { REPORT_HELP } from "../constants/reportHelpContent";
import {
  getReportPathForRoute,
  getReportsHomePathForRoute,
} from "../utils/reportRouteContext";

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const parts = formatter.formatToParts(date);
      const lookup = { year: "", month: "", day: "" };
      for (const part of parts) {
        if (Object.prototype.hasOwnProperty.call(lookup, part.type)) {
          lookup[part.type] = part.value;
        }
      }
      if (lookup.year && lookup.month && lookup.day) {
        return `${lookup.year}-${lookup.month}-${lookup.day}`;
      }
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateFromParts = (year, month, day) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const addDaysToIsoDate = (dateStr, deltaDays) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts.map((value) => Number(value));
  if (![year, month, day].every((value) => Number.isFinite(value))) return "";
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCDate(base.getUTCDate() + deltaDays);
  return formatDateFromParts(
    base.getUTCFullYear(),
    base.getUTCMonth() + 1,
    base.getUTCDate()
  );
};

const isInvalidDateRange = (startValue, endValue) => {
  if (!startValue || !endValue) return false;
  return endValue < startValue;
};

const formatMoney = (value) => {
  const number = Number(value ?? 0);
  if (Number.isNaN(number)) return "$0.00";
  const fixed = Math.abs(number).toFixed(2);
  return number < 0 ? `-$${fixed}` : `$${fixed}`;
};

export default function CashBalanceReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSalon } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.CASH_BALANCE;
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );
  const today = useMemo(
    () => formatDateISO(new Date(), salonTimezone),
    [salonTimezone]
  );
  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate(getReportsHomePathForRoute(location.pathname), { replace: true });
  };
  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={CASH_BALANCE_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );
  const defaultRange = useMemo(() => {
    if (!today) {
      return { start: "", end: "" };
    }
    const year = Number(today.slice(0, 4));
    const month = Number(today.slice(5, 7));
    return {
      start: formatDateFromParts(year, month, 1),
      end: today,
    };
  }, [today]);

  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [dateRangeError, setDateRangeError] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState("");

  useEffect(() => {
    if (defaultRange.start && !startDate) {
      setStartDate(defaultRange.start);
    }
    if (defaultRange.end && !endDate) {
      setEndDate(defaultRange.end);
    }
  }, [defaultRange, startDate, endDate]);

  useEffect(() => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
    } else {
      setDateRangeError("");
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    if (isInvalidDateRange(startDate, endDate)) return;
    let mounted = true;
    const loadReport = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const data = await getCashBalanceReport({
          start: startDate,
          end: endDate,
        });
        if (!mounted) return;
        setReport(data);
      } catch (error) {
        if (!mounted) return;
        setLoadError("Unable to load cash balance report.");
        setReport(null);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    loadReport();
    return () => {
      mounted = false;
    };
  }, [startDate, endDate]);

  const handleExport = async () => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    setExportError("");
    setExportLoading(true);
    try {
      await exportCashBalanceReport({
        start: startDate,
        end: endDate,
      });
    } catch (error) {
      setExportError("Unable to export CSV.");
    } finally {
      setExportLoading(false);
    }
  };

  const setRange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const setTodayRange = () => setRange(today, today);
  const setLast7DaysRange = () => {
    const start = addDaysToIsoDate(today, -6);
    setRange(start, today);
  };
  const setThisMonthRange = () => {
    const parts = today.split("-");
    if (parts.length !== 3) return;
    const start = formatDateFromParts(parts[0], parts[1], 1);
    setRange(start, today);
  };
  const setLastMonthRange = () => {
    const year = Number(today.slice(0, 4));
    const month = Number(today.slice(5, 7));
    if (!Number.isFinite(year) || !Number.isFinite(month)) return;
    let prevYear = year;
    let prevMonth = month - 1;
    if (prevMonth < 1) {
      prevMonth = 12;
      prevYear -= 1;
    }
    const endDay = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
    const start = formatDateFromParts(prevYear, prevMonth, 1);
    const end = formatDateFromParts(prevYear, prevMonth, endDay);
    setRange(start, end);
  };
  const setThisYearRange = () => {
    const year = Number(today.slice(0, 4));
    if (!Number.isFinite(year)) return;
    setRange(formatDateFromParts(year, 1, 1), today);
  };
  const setLastYearRange = () => {
    const year = Number(today.slice(0, 4)) - 1;
    if (!Number.isFinite(year)) return;
    setRange(formatDateFromParts(year, 1, 1), formatDateFromParts(year, 12, 31));
  };

  const rows = report?.rows || [];
  const meta = report?.meta || {};
  const asOfDate = meta.as_of_date || endDate;
  const cashBalanceColumns = useMemo(
    () => ({
      date: { type: "date", getValue: (row) => row?.date },
      total_sales: { type: "number", getValue: (row) => row?.total_sales },
      cash_revenue: { type: "number", getValue: (row) => row?.cash_revenue },
      cash_expense: { type: "number", getValue: (row) => row?.cash_expense },
      category: { type: "text", getValue: (row) => row?.category || "" },
      notes: { type: "text", getValue: (row) => row?.notes || "" },
    }),
    []
  );
  const cashBalanceSort = useTableSort({
    rows,
    columns: cashBalanceColumns,
    fallbackKey: "date",
  });
  const handleDateClick = (dateValue) => {
    const normalizedDate = toISODateString(dateValue);
    if (!normalizedDate) return;
    navigate(
      `${getReportPathForRoute(
        location.pathname,
        "/manager/reports/daily-closing"
      )}?work_date=${encodeURIComponent(
        normalizedDate
      )}`,
      {
        state: { preselectedWorkDateISO: normalizedDate, source: "cash-balance" },
      }
    );
  };

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Cash Balance Report
            </div>
            <div className="flex items-center gap-3">
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div id="cash-balance-overview" className={getSectionClassName("cash-balance-overview")}>
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_cash_balance"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            <div
              id="cash-balance-filters"
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-5 ${getSectionClassName(
                "cash-balance-filters"
              )}`}
            >
              <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto] items-end">
                <div className="flex flex-col gap-1">
                  {renderFieldLabel("Start Date", "start_date")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  {renderFieldLabel("End Date", "end_date")}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <button
                  id="cash-balance-export"
                  type="button"
                  onClick={handleExport}
                  className={`${BTN_OUTLINE} ${getSectionClassName("cash-balance-export")}`}
                  disabled={exportLoading}
                >
                  {exportLoading ? "Exporting..." : "Export CSV"}
                </button>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm md:text-base">
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-500">Quick pick:</span>
                  <InfoTooltip text={CASH_BALANCE_TOOLTIPS.quick_pick} label="Quick pick" />
                </div>
                <button
                  type="button"
                  onClick={setTodayRange}
                  className="text-rose-600 font-semibold hover:text-rose-700"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={setLast7DaysRange}
                  className="text-rose-600 font-semibold hover:text-rose-700"
                >
                  Last 7 days
                </button>
                <button
                  type="button"
                  onClick={setThisMonthRange}
                  className="text-rose-600 font-semibold hover:text-rose-700"
                >
                  This month
                </button>
                <button
                  type="button"
                  onClick={setLastMonthRange}
                  className="text-rose-600 font-semibold hover:text-rose-700"
                >
                  Last month
                </button>
                <button
                  type="button"
                  onClick={setThisYearRange}
                  className="text-rose-600 font-semibold hover:text-rose-700"
                >
                  This year
                </button>
                <button
                  type="button"
                  onClick={setLastYearRange}
                  className="text-rose-600 font-semibold hover:text-rose-700"
                >
                  Last year
                </button>
              </div>
              {dateRangeError && (
                <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
                  {dateRangeError}
                </div>
              )}
              {exportError && (
                <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
                  {exportError}
                </div>
              )}
            </div>

            {loadError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {loadError}
              </div>
            )}

            {loading && (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-600 text-sm md:text-base">
                Loading cash balance report...
              </div>
            )}

            {!loading && report && (
              <div
                id="cash-balance-kpis"
                className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${getSectionClassName(
                  "cash-balance-kpis"
                )}`}
              >
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Cash Balance
                </div>
                <div className="mt-2 text-3xl md:text-4xl font-semibold text-slate-900">
                  {formatMoney(meta.ending_cash_balance)}
                </div>
                <div className="mt-1 text-sm md:text-base text-slate-600">
                  AS OF {asOfDate}
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">
                      Opening Cash Balance
                    </span>
                    <span className="font-semibold text-slate-900">
                      {formatMoney(meta.opening_cash_balance)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Net Change</span>
                    <span className="font-semibold text-slate-900">
                      {formatMoney(meta.net_change)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div
              id="cash-balance-details"
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName(
                "cash-balance-details"
              )}`}
            >
              <div className="px-4 py-3 border-b border-slate-200 text-base md:text-lg font-semibold text-slate-900 flex items-center gap-2">
                <span>Cash Balance Detail</span>
                <InfoTooltip text={CASH_BALANCE_TOOLTIPS.report_scope} label="Cash Balance Detail" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <SortableTh
                        label="Date"
                        sortKey="date"
                        sortState={cashBalanceSort.sortState}
                        onSort={cashBalanceSort.handleSort}
                        tooltip={CASH_BALANCE_TOOLTIPS.column_date}
                        className="px-4 py-3 text-sm md:text-base"
                        align="left"
                      />
                      <SortableTh
                        label="Total Sales"
                        sortKey="total_sales"
                        sortState={cashBalanceSort.sortState}
                        onSort={cashBalanceSort.handleSort}
                        tooltip={CASH_BALANCE_TOOLTIPS.column_total_sales}
                        className="px-4 py-3 text-sm md:text-base"
                        align="left"
                      />
                      <SortableTh
                        label="Cash Revenue"
                        sortKey="cash_revenue"
                        sortState={cashBalanceSort.sortState}
                        onSort={cashBalanceSort.handleSort}
                        tooltip={CASH_BALANCE_TOOLTIPS.column_cash_revenue}
                        className="px-4 py-3 text-sm md:text-base"
                        align="left"
                      />
                      <SortableTh
                        label="Cash Expense"
                        sortKey="cash_expense"
                        sortState={cashBalanceSort.sortState}
                        onSort={cashBalanceSort.handleSort}
                        tooltip={CASH_BALANCE_TOOLTIPS.column_cash_expense}
                        className="px-4 py-3 text-sm md:text-base"
                        align="left"
                      />
                      <SortableTh
                        label="Category"
                        sortKey="category"
                        sortState={cashBalanceSort.sortState}
                        onSort={cashBalanceSort.handleSort}
                        tooltip={CASH_BALANCE_TOOLTIPS.column_category}
                        className="px-4 py-3 text-sm md:text-base"
                        align="left"
                      />
                      <SortableTh
                        label="Notes"
                        sortKey="notes"
                        sortState={cashBalanceSort.sortState}
                        onSort={cashBalanceSort.handleSort}
                        tooltip={CASH_BALANCE_TOOLTIPS.column_notes}
                        className="px-4 py-3 text-sm md:text-base"
                        align="left"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-4 py-4 text-sm md:text-base text-slate-500"
                        >
                          Loading rows...
                        </td>
                      </tr>
                    ) : cashBalanceSort.sortedRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-4 py-4 text-sm md:text-base text-slate-500"
                        >
                          No cash activity found for this range.
                        </td>
                      </tr>
                    ) : (
                      cashBalanceSort.sortedRows.map((row, index) => (
                        <tr
                          key={`${row.date || "date"}-${index}`}
                          className="border-t border-slate-200"
                        >
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                            {row.date ? (
                              <button
                                type="button"
                                onClick={() => handleDateClick(row.date)}
                                className="text-rose-600 hover:underline hover:text-rose-700 cursor-pointer"
                              >
                                {row.date}
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {formatMoney(row.total_sales)}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {formatMoney(row.cash_revenue)}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {formatMoney(row.cash_expense)}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {row.category || ""}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {row.notes || ""}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
